import React from "react";
import { useTranslation } from "react-i18next";

// These are labels, not controls, so they stay out of the tab order.
// The hover effect is applied unconditionally and then neutralised for touch-only devices
// by the `@media (hover: none)` block in index.css. That direction is deliberate: if a
// browser reports its hover capability oddly, the default is the working effect rather
// than silently no effect.
const Tag = ({ children }) => (
  <span
    className="skill-tag group inline-flex items-center font-mono text-xs px-2.5 py-1.5 rounded border border-border bg-surface/40 text-text cursor-default select-none
               transition duration-200 ease-out
               hover:border-cyan hover:text-cyan hover:bg-cyan/10
               motion-safe:hover:-translate-y-0.5 motion-safe:hover:scale-105"
  >
    {children}
  </span>
);

const Skills = () => {
  const { t } = useTranslation();

  const languageTiers = [
    { labelKey: "skills.advanced", items: [t("skills.languages.c"), t("skills.languages.cpp")] },
    {
      labelKey: "skills.intermediate",
      items: [t("skills.languages.python"), t("skills.languages.matlab")],
    },
    {
      labelKey: "skills.basic",
      items: [
        t("skills.languages.java"),
        t("skills.languages.htmlcss"),
        t("skills.languages.sql"),
        t("skills.languages.bash"),
        t("skills.languages.gradle"),
      ],
    },
  ];

  const skillGroups = [
    {
      labelKey: "skills.tools",
      items: [
        t("skills.toolsList.git"),
        t("skills.toolsList.linux"),
        t("skills.toolsList.fusion360"),
        t("skills.toolsList.bambuLab"),
        t("skills.toolsList.ccs"),
      ],
    },
    {
      labelKey: "skills.technical",
      items: [
        t("skills.technicalList.embedded"),
        t("skills.technicalList.pcb"),
        t("skills.technicalList.prototyping"),
        t("skills.technicalList.automotive"),
      ],
    },
    {
      labelKey: "skills.softSkills",
      items: [
        t("skills.softSkillsList.problemSolving"),
        t("skills.softSkillsList.communication"),
        t("skills.softSkillsList.adaptability"),
        t("skills.softSkillsList.leadership"),
        t("skills.softSkillsList.creativity"),
      ],
    },
  ];

  const spokenLanguages = [
    { name: t("skills.spokenLanguagesList.romanian"), level: t("skills.spokenLanguagesList.native") },
    { name: t("skills.spokenLanguagesList.english"), level: t("skills.spokenLanguagesList.advancedLevel") },
    { name: t("skills.spokenLanguagesList.german"), level: t("skills.spokenLanguagesList.elementary") },
  ];

  return (
    <div className="mt-8 pt-8 border-t border-border">
      <h3 className="text-2xl font-semibold text-text mb-6">{t("skills.title")}</h3>

      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-wide text-muted mb-3">
          {t("skills.programmingLanguages")}
        </p>
        <div className="flex flex-col gap-3">
          {languageTiers.map((tier) => (
            <div key={tier.labelKey} className="flex flex-wrap items-baseline gap-3">
              <span className="text-xs text-muted italic w-full sm:w-24 sm:shrink-0">
                {t(tier.labelKey)}
              </span>
              <div className="flex flex-wrap gap-2">
                {tier.items.map((item) => (
                  <Tag key={item}>{item}</Tag>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-8 mb-8">
        {skillGroups.map((group) => (
          <div key={group.labelKey}>
            <p className="font-mono text-xs uppercase tracking-wide text-muted mb-3">
              {t(group.labelKey)}
            </p>
            <div className="flex flex-wrap gap-2">
              {group.items.map((item) => (
                <Tag key={item}>{item}</Tag>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div>
        <p className="font-mono text-xs uppercase tracking-wide text-muted mb-3">
          {t("skills.spokenLanguages")}
        </p>
        <div className="flex flex-wrap gap-2">
          {spokenLanguages.map((lang) => (
            <Tag key={lang.name}>
              {lang.name}{" "}
              <span className="skill-tag-level ml-1 text-muted transition-colors duration-200 group-hover:text-cyan">
                · {lang.level}
              </span>
            </Tag>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Skills;
